import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { AlertTriangle, ChevronDown, ChevronRight, Copy, Check, Eye, Loader2 } from 'lucide-react';
import { PLACEHOLDERS, unknownPlaceholders } from '@shared/placeholders.js';
import { base44 } from '@/api/base44Client';
import { toast } from 'sonner';
import EmailBodyEditor from '@/components/EmailBodyEditor';

/**
 * The three message template boxes, wherever they appear.
 *
 * The same component backs the per show fields on the Shows screen and the
 * global and per rep panels on Templates, so the list of available
 * placeholders is written once and cannot say different things in two places.
 * It used to: the Templates screen advertised {zoomJoinUrl} and {dateStr},
 * which nothing fills, and never mentioned {dayOfWeek} or {calendarLink},
 * which everything does.
 *
 * A placeholder the renderer cannot fill is flagged as you type, because the
 * consequence is silent. render() drops any line holding a placeholder it has
 * no value for, so a typo does not reach the prospect as "{zoomJoinUrl}": the
 * entire sentence disappears from the message and nothing says so.
 */

function Unknown({ names }) {
  if (names.length === 0) return null;
  return (
    <p className="flex items-start gap-1.5 text-[11px] text-amber-500 mt-1">
      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
      <span>
        {names.length === 1 ? 'Not a placeholder: ' : 'Not placeholders: '}
        <span className="font-mono">{names.map(n => `{${n}}`).join(' ')}</span>
        . {names.length === 1 ? 'Its line' : 'Those lines'} will be left out of the
        message entirely.
      </span>
    </p>
  );
}

/**
 * One placeholder token, copyable in one click instead of highlighting it by
 * hand. `{name}` is dark amber text on a light amber chip rather than the
 * theme's orange accent color on plain white: at 11px, accent-on-white read
 * as barely legible, roughly 2.5:1 contrast, well under the 4.5:1 a body of
 * text needs.
 */
function PlaceholderToken({ name }) {
  const [copied, setCopied] = useState(false);
  const token = `{${name}}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // Clipboard access can be denied (permissions, insecure context). The
      // token is still on screen to select by hand, so this fails quietly.
    }
  };

  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 border border-amber-200 pl-1.5 pr-1 py-0.5 shrink-0">
      <span className="font-mono text-sm font-semibold text-amber-800">{token}</span>
      <button
        type="button"
        onClick={copy}
        title={`Copy ${token}`}
        aria-label={`Copy ${token}`}
        className="p-0.5 rounded text-amber-700 hover:bg-amber-200/60 hover:text-amber-900"
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </span>
  );
}

function Reference() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg bg-secondary/40 p-2.5">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 text-xs font-medium text-foreground"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        Available placeholders ({PLACEHOLDERS.length})
      </button>
      {open && (
        <dl className="mt-2 space-y-2">
          {PLACEHOLDERS.map(p => (
            <div key={p.name} className="flex items-start gap-2 text-xs leading-relaxed">
              <dt className="shrink-0"><PlaceholderToken name={p.name} /></dt>
              <dd className="text-muted-foreground pt-1">
                {p.describes}
                {!p.channels.includes('sms') && <span className="opacity-60"> (email only)</span>}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {!open && (
        <p className="text-[10px] text-muted-foreground mt-1">
          Leave a box blank to use the level below it.
        </p>
      )}
    </div>
  );
}

/**
 * What a blank box actually resolves to and why, worked out once per field
 * so the box, its label, and its styling can never disagree with each other.
 *
 * `rawValue` is what is actually stored for this level (blank means nothing
 * stored here). `resolvedInfo` is `{ source, value, label }` describing what
 * the next level down would supply, or null when the caller has not wired
 * inheritance preview up for this field (the Default panel: there is no
 * level below it but the built-in wording, not worth the same treatment).
 *
 * Typing into a box always calls the same onChange as before with the box's
 * literal contents. A ghost box's displayed value is the inherited text, so
 * typing into it starts from that text and the moment onChange fires, the
 * result is stored as this level's own real value, exactly like typing into
 * an empty box always has. Nothing here changes what gets saved, only what
 * is shown before anyone types.
 */
function fieldMeta(rawValue, resolvedInfo, overrideLabel) {
  const hasOwnValue = !!(rawValue || '').trim();

  if (hasOwnValue) {
    return {
      displayValue: rawValue,
      isGhost: false,
      label: overrideLabel ? `Overriding for ${overrideLabel}` : null,
      placeholder: 'Leave blank to use the level below',
    };
  }

  if (!resolvedInfo) {
    return { displayValue: '', isGhost: false, label: null, placeholder: 'Leave blank to use the level below' };
  }

  if (resolvedInfo.source === 'none') {
    return {
      displayValue: '',
      isGhost: false,
      label: null,
      placeholder: resolvedInfo.label,
    };
  }

  return {
    displayValue: resolvedInfo.value,
    isGhost: true,
    label: `Inherited from ${resolvedInfo.label}`,
    placeholder: 'Leave blank to use the level below',
  };
}

/** Small label above a box, styled by whether it names a real override or an inherited value. */
function FieldLabel({ meta }) {
  if (!meta.label) return null;
  return (
    <p className={`text-[10px] mb-1 ${meta.isGhost ? 'text-muted-foreground italic' : 'text-accent font-semibold'}`}>
      {meta.isGhost ? meta.label : `● ${meta.label}`}
    </p>
  );
}

const ghostClass = 'text-muted-foreground/70 italic';

/**
 * Renders the text message against a fabricated example contact (task 9 of
 * the 2026-09-06 batch), the same fixture worker/lib/reminders.ts's email
 * preview already uses. Previews whatever the box currently shows,
 * including inherited ghost text, since that is genuinely what would send
 * if nothing is typed here.
 *
 * previewRepId, when given (task: real rep data in the Templates preview,
 * 2026-09-24), renders against that rep's own real name, phone, email and
 * signature instead of the fabricated example rep. previewShow, `{ name,
 * location }`, when given (task: real show data in the Per-Event Override
 * preview, same day), renders against that show's own real name and
 * location instead of SAMPLE_SHOW's fake ones; only EventEmailEditor's own
 * use of this component (the Per-Event Override panel, the one screen with
 * a real show in scope) ever passes one. The contact side (name, meeting
 * topics) stays fabricated either way, since there is no real contact being
 * previewed here.
 */
function SmsPreview({ template, previewRepId, previewShow }) {
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      setPreview(await base44.reminders.previewSms(template || '', previewRepId, previewShow));
    } catch (err) {
      toast.error(err.message || 'Could not render a preview');
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="mt-1.5">
      <Button type="button" variant="outline" size="sm" onClick={handlePreview} disabled={previewing} className="h-7 text-[11px]">
        {previewing ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Eye className="w-3 h-3 mr-1" />Preview text</>}
      </Button>
      {preview && (
        <div className="mt-1.5 rounded-lg border border-border bg-secondary/30 p-2.5">
          <p className="text-sm text-foreground whitespace-pre-line">{preview.body}</p>
          <p className="text-[10px] text-muted-foreground mt-1.5">
            {preview.segments} segment{preview.segments === 1 ? '' : 's'}, against a fabricated example contact
            {previewRepId ? '' : ' and rep'}{previewShow ? ` at ${previewShow.name}` : ''}. Nothing was sent.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * The email equivalent of SmsPreview above, task: add a "Preview email"
 * button next to Email subject/body (2026-09-24). There was no way to see
 * the Initial Email rendered from the Default or Per-Rep panels at all
 * before this; EventEmailEditor.jsx already has its own separate preview
 * (and its own "Send test to me"), which is why this only renders when a
 * caller explicitly asks for it via `show` rather than always: TemplateFields
 * is also the field set EventEmailEditor wraps, and unconditionally adding
 * a second preview button here would have shown two on every one of its
 * four tabs.
 *
 * Always renders against kind 'initial': the Default and Per-Rep panels
 * (the only callers that pass `show`) apply to the text message and Initial
 * Email only, never the three reminder stages (Templates.jsx's own note on
 * the Default panel).
 */
function EmailPreview({ show, subject, body, previewRepId }) {
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  if (!show) return null;

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      setPreview(await base44.reminders.previewEmail('initial', subject || '', body || '', previewRepId));
    } catch (err) {
      toast.error(err.message || 'Could not render a preview');
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="mt-1.5">
      <Button type="button" variant="outline" size="sm" onClick={handlePreview} disabled={previewing} className="h-7 text-[11px]">
        {previewing ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Eye className="w-3 h-3 mr-1" />Preview email</>}
      </Button>
      {preview && (
        <div className="mt-1.5 rounded-xl border border-border overflow-hidden">
          <div className="bg-secondary/40 px-3 py-2 border-b border-border">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Subject</p>
            <p className="text-sm font-medium text-foreground">{preview.subject}</p>
          </div>
          {/* sandboxed, no scripts: same reasoning as EventEmailEditor.jsx's
              own preview iframe, this is the admin's own typed HTML,
              rendered back to them. */}
          <iframe
            title="Email preview"
            sandbox=""
            srcDoc={preview.html}
            className="w-full h-72 bg-white"
          />
          <p className="text-[10px] text-muted-foreground px-3 py-1.5 bg-secondary/20">
            Rendered against a fabricated example contact (Jane Smith, Example Trade Show)
            {previewRepId ? '' : ' and rep'}. Nothing was sent.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * `value` is { sms, subject, body }. Kept neutral rather than using the
 * database column names, because the two callers store them under different
 * keys: shows have sms_template, the settings document has sms.
 *
 * `showSms` hides the text message box entirely. There is exactly one SMS,
 * tied to the Initial Email at capture time; the three reminder stages
 * (task 3.6 follow up) are email only, so their editors pass `showSms={false}`
 * rather than showing a text box that nothing ever sends.
 *
 * `resolved` and `overrideLabel` turn on the inherited-value preview: pass
 * `resolved={{ sms, subject, body }}`, each either null (this level has
 * nothing further to fall back to worth naming) or `{ source, value, label }`
 * describing the next real level down, and `overrideLabel` naming what a non
 * blank box here is an override for (a show's name, a rep's name). Omit both
 * to keep the old generic "leave blank" placeholder, which is what the
 * Default panel still uses: there is nothing named below it but the built in
 * wording.
 *
 * `showEmailPreview` (task: add a "Preview email" button, 2026-09-24) turns
 * on the email preview button below the body box, same idea as `showSms`
 * above. Only Templates.jsx's Default and Per-Rep panels pass it; see
 * EmailPreview's own comment for why it defaults off. `previewRepId` (task:
 * real rep data in the Templates preview, same day) and `previewShow` (task:
 * real show data in the Per-Event Override preview, same day) are threaded
 * into the text preview: the real rep and/or show to render against,
 * instead of the fabricated example ones, whenever a caller has one to
 * name. EmailPreview only ever receives previewRepId, not previewShow: it
 * is never rendered for the one caller (EventEmailEditor) that has a real
 * show, since that caller shows its own separate email preview instead (see
 * EmailPreview's own comment).
 */
export default function TemplateFields({ value, onChange, disabled = false, idPrefix = 'tpl', showSms = true, resolved = null, overrideLabel = null, showEmailPreview = false, previewRepId = null, previewShow = null }) {
  const set = (key, next) => onChange({ ...value, [key]: next });

  const smsMeta = fieldMeta(value.sms, resolved?.sms, overrideLabel);
  const subjectMeta = fieldMeta(value.subject, resolved?.subject, overrideLabel);
  const bodyMeta = fieldMeta(value.body, resolved?.body, overrideLabel);

  return (
    <div className="space-y-3">
      <Reference />

      {showSms && (
      <div>
        <Label htmlFor={`${idPrefix}-sms`} className="text-xs text-muted-foreground">
          Text message
        </Label>
        <FieldLabel meta={smsMeta} />
        <Textarea
          id={`${idPrefix}-sms`}
          value={smsMeta.displayValue}
          onChange={e => set('sms', e.target.value)}
          disabled={disabled}
          rows={8}
          placeholder={smsMeta.placeholder}
          className={`mt-1 md:text-sm resize-none ${smsMeta.isGhost ? ghostClass : ''}`}
        />
        <Unknown names={unknownPlaceholders(value.sms, 'sms')} />
        <SmsPreview template={smsMeta.displayValue} previewRepId={previewRepId} previewShow={previewShow} />
      </div>
      )}

      <div>
        <Label htmlFor={`${idPrefix}-subject`} className="text-xs text-muted-foreground">
          Email subject
        </Label>
        <FieldLabel meta={subjectMeta} />
        <Input
          id={`${idPrefix}-subject`}
          value={subjectMeta.displayValue}
          onChange={e => set('subject', e.target.value)}
          disabled={disabled}
          placeholder={subjectMeta.placeholder}
          className={`mt-1 h-9 md:text-sm ${subjectMeta.isGhost ? ghostClass : ''}`}
        />
        <Unknown names={unknownPlaceholders(value.subject, 'email')} />
      </div>

      <div>
        <Label htmlFor={`${idPrefix}-body`} className="text-xs text-muted-foreground">
          Email body (HTML)
        </Label>
        <FieldLabel meta={bodyMeta} />
        <EmailBodyEditor
          id={`${idPrefix}-body`}
          value={bodyMeta.displayValue}
          onChange={html => set('body', html)}
          disabled={disabled}
          placeholder={bodyMeta.placeholder}
          isGhost={bodyMeta.isGhost}
          ghostClassName={ghostClass}
        />
        <Unknown names={unknownPlaceholders(value.body, 'email')} />
        <EmailPreview
          show={showEmailPreview}
          subject={subjectMeta.displayValue}
          body={bodyMeta.displayValue}
          previewRepId={previewRepId}
        />
      </div>
    </div>
  );
}
